import { WitboostEntity } from '@agilelab/plugin-wb-builder-common';
import { Entity } from '@backstage/catalog-model';
import { ConfigApi } from '@backstage/core-plugin-api';
import {
  EntityTextFilter,
  EntityOrphanFilter as BackstageEntityOrphanFilter,
  EntityFilter,
} from '@backstage/plugin-catalog-react';

export class EntitySearchTextFilter extends EntityTextFilter {
  constructor(value: string, _: ConfigApi) {
    super(value);
  }

  /**
   * frontend filter, this is called after getFullTextFilters
   * @param entity
   */
  filterEntity(entity: Entity): boolean {
    const defaultFilter = super.filterEntity(entity);
    const lowercaseValue = this.value.toLowerCase();

    // default filter does not take into account the description frontend side
    const descriptionPresent = entity.metadata.description
      ?.toLowerCase()
      .includes(lowercaseValue);

    // default filter does not take into account the mesh name frontend side
    const meshNamePresent = (entity as WitboostEntity).spec?.mesh?.name
      ?.toLowerCase()
      .includes(lowercaseValue);

    return descriptionPresent || defaultFilter || meshNamePresent;
  }

  /**
   * Delegate search to backend by passing the search terms and fields to search in.
   */
  getFullTextFilters(): { term: string; fields: string[] } {
    const customFilters = super.getFullTextFilters();
    customFilters.fields = [];
    customFilters.fields.push('metadata.name');
    customFilters.fields.push('metadata.tags');
    customFilters.fields.push('metadata.description');
    customFilters.fields.push('spec.mesh.name');
    customFilters.fields.push('spec.mesh.tags');
    // for users
    customFilters.fields.push('spec.profile.displayName');
    customFilters.fields.push('spec.profile.email');
    return customFilters;
  }
}
/**
 * Extend the base class of Backstage and add `toQueryValue` that adds the value as query param
 * and `getCatalogFilters` that performs the query to the backend.
 */
export class EntityErrorFilter implements EntityFilter {
  readonly value: boolean;
  constructor(value: boolean) {
    this.value = value;
  }
  toQueryValue(): string {
    return this.value.toString();
  }

  filterEntity(entity: any) {
    const error = entity?.status?.items?.length > 0;
    return error !== void 0 && this.value === error;
  }
  // TODO:: This function is here only to trigger the filter on the backend side and recalculate the entities list , because only changing the frontend filters won't update the entities, see (plugins/catalog-react/src/hooks/useEntityListProvider.tsx - 283 - in backstage).
  // The filter below is not included and won't harm the query because its value is an object. See packages/catalog-client/src/CatalogClient.ts getFilterValue
  // Ideally this should be a backend filter
  //
  getCatalogFilters(): Record<string, any> {
    if (this.value) {
      return { 'status.items': { _not_really_working: true } };
    }
    return {};
  }
}

/**
 * Extend the base class of Backstage EntityOrphanFilter and add `toQueryValue` that adds the value as query param.
 */
export class EntityOrphanFilter extends BackstageEntityOrphanFilter {
  toQueryValue() {
    return this.value;
  }
}

export class EntityServiceAccountFilter implements EntityFilter {
  private value: boolean;

  constructor(value: boolean) {
    this.value = value;
  }

  filterEntity(entity: any) {
    const serviceAccount = entity?.spec?.isServiceAccount;
    return serviceAccount !== void 0 && this.value === serviceAccount;
  }

  getCatalogFilters(): Record<string, string> {
    return { 'spec.isServiceAccount': `${this.value}` };
  }
}

export class EntityUserFilter implements EntityFilter {
  private value: string[];

  constructor() {
    this.value = ['user'];
  }

  filterEntity(entity: any) {
    const serviceAccount = entity?.spec?.isServiceAccount;
    return serviceAccount !== true;
  }

  getCatalogFilters() {
    return { kind: this.value };
  }
}

export class EntityRefreshFilter implements EntityFilter {
  readonly value: string;
  constructor(value: string) {
    this.value = value;
  }
  // TODO:: This function is here only to trigger the filter on the backend side and recalculate the entities list , because only changing the frontend filters won't update the entities, see (plugins/catalog-react/src/hooks/useEntityListProvider.tsx - 283 - in backstage).
  // The filter below is not included and won't harm the query because its value is an object. See packages/catalog-client/src/CatalogClient.ts getFilterValue
  // this.value will change with a random value to trigger the refresh of the entities list
  getCatalogFilters(): Record<string, any> {
    if (this.value) {
      return { 'status.items': { _not_really_working: this.value } };
    }
    return {};
  }
}
